import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useHistory } from '../hooks/useHistory';
import { useAlert } from '../hooks/useAlert';
import { useFolders } from '../hooks/useFolders';
import Header from '../components/Header';
import QuotaDisplay from '../components/QuotaDisplay';
import CustomAlert from '../components/CustomAlert';
import TranscriptNameEditDialog from '../components/TranscriptNameEditDialog';
import FolderSidebar from '../components/FolderSidebar';
import BulkActionBar from '../components/BulkActionBar';
import MoveToFolderDialog from '../components/MoveToFolderDialog';
import ShareableTranscriptCard from '../components/ShareableTranscriptCard';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import './Dashboard.css';
import { debugLog } from '../utils/debug';

function Dashboard() {
  const [transcripts, setTranscripts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchDialogOpen, setIsSearchDialogOpen] = useState(false);
  const [selectedTranscripts, setSelectedTranscripts] = useState([]);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filterTag, setFilterTag] = useState('');
  const [error, setError] = useState('');
  const [editingTitle, setEditingTitle] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [nameValidationError, setNameValidationError] = useState('');
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [totalHours, setTotalHours] = useState('0.0');
  const [totalTranscripts, setTotalTranscripts] = useState(0);
  const [monthlyUsage, setMonthlyUsage] = useState({
    transcripts: 0,
    hours: '0.0',
    monthKey: ''
  });
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMoveDialogOpen, setIsMoveDialogOpen] = useState(false);

  // Share card state
  const [showShareCard, setShowShareCard] = useState(false);
  const [shareCardData, setShareCardData] = useState(null);
  const [shareCardTheme, setShareCardTheme] = useState('gradient-blue');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreTranscripts, setHasMoreTranscripts] = useState(false);
  const [totalTranscriptCount, setTotalTranscriptCount] = useState(0);
  const TRANSCRIPTS_PER_PAGE = 50;

  // Correction requests state
  const [correctionRequests, setCorrectionRequests] = useState([]);
  const [showCorrectionRequests, setShowCorrectionRequests] = useState(false);

  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const { cloneTranscript } = useHistory();
  const { alert, showConfirm, showSuccess, showError, showShareLink } = useAlert();
  const {
    folderTree,
    currentFolderId,
    folderPath,
    setCurrentFolderId,
    createFolder,
    updateFolder,
    deleteFolder,
    moveFolder,
    moveTranscriptsToFolder,
    bulkDeleteTranscripts,
    fetchFolders
  } = useFolders();
  const navigate = useNavigate();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdownId && !event.target.closest('.dropdown-container')) {
        setOpenDropdownId(null);
      }
    };

    if (openDropdownId) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openDropdownId]);

  // Listen for share card theme changes
  useEffect(() => {
    const handleThemeChange = (e) => {
      setShareCardTheme(e.detail);
    };
    window.addEventListener('shareCardThemeChange', handleThemeChange);
    return () => window.removeEventListener('shareCardThemeChange', handleThemeChange);
  }, []);

  useEffect(() => {
    // Wait for auth to finish loading before redirecting
    if (authLoading) {
      return;
    }

    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    
    // Allow all authenticated users to access dashboard
    // Free plan users should be able to see their transcripts
    
    fetchTranscripts();
    fetchTotalHours();
    fetchTotalTranscripts();
    fetchMonthlyUsage();
  }, [isAuthenticated, authLoading, user, navigate]);

  // Refetch when sort options change
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      setCurrentPage(1);
      fetchTranscripts(1, false);
    }
  }, [sortBy, sortOrder]);

  const fetchTotalHours = async () => {
    try {
      const response = await fetch('/api/usage/total-hours', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTotalHours(data.totalHours);
      } else {
        console.error('Failed to load total hours:', response.status, response.statusText);
      }
    } catch (err) {
      console.error('Error fetching total hours:', err);
    }
  };

  const fetchTotalTranscripts = async () => {
    try {
      const response = await fetch('/api/usage/total-transcripts', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTotalTranscripts(data.totalTranscripts);
      } else {
        console.error('Failed to load total transcripts:', response.status, response.statusText);
      }
    } catch (err) {
      console.error('Error fetching total transcripts:', err);
    }
  };

  const fetchMonthlyUsage = async () => {
    try {
      const response = await fetch('/api/usage/current-month', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setMonthlyUsage({
          transcripts: data.monthlyTranscripts,
          hours: data.monthlyHours,
          monthKey: data.monthKey
        });
      } else {
        console.error('Failed to load monthly usage:', response.status, response.statusText);
      }
    } catch (err) {
      console.error('Error fetching monthly usage:', err);
    }
  };

  const fetchTranscripts = async (page = 1, append = false) => {
    try {
      if (append) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
      }

      const params = new URLSearchParams({
        page: page.toString(),
        limit: TRANSCRIPTS_PER_PAGE.toString(),
        sortBy: sortBy === 'createdAt' ? 'created_at' : 'updated_at',
        sortOrder: sortOrder.toUpperCase()
      });

      const response = await fetch(`/api/transcripts?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (response.ok) {
        const data = await response.json();

        // Handle paginated response
        if (data.transcripts && data.pagination) {
          if (append) {
            setTranscripts(prev => [...prev, ...data.transcripts]);
          } else {
            setTranscripts(data.transcripts);
          }
          setHasMoreTranscripts(data.pagination.hasMore);
          setTotalTranscriptCount(data.pagination.total);
          setCurrentPage(data.pagination.page);
        } else {
          // Backward compatibility: handle array response
          setTranscripts(data);
          setHasMoreTranscripts(false);
          setTotalTranscriptCount(data.length);
        }
      } else {
        let errorMessage = `Failed to load transcripts (${response.status})`;
        try {
          const errorData = await response.json();
          errorMessage += `: ${errorData.error || errorData.message || 'Unknown error'}`;
        } catch (jsonError) {
          const textError = await response.text();
          errorMessage += `: ${textError || response.statusText}`;
        }
        console.error('[Dashboard] API Error:', response.status, errorMessage);
        setError(errorMessage);
      }
    } catch (err) {
      console.error('[Dashboard] Network Error:', err);
      setError(`Error loading transcripts: ${err.message}`);
      console.error('Error fetching transcripts:', err);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const loadMoreTranscripts = () => {
    if (hasMoreTranscripts && !isLoadingMore) {
      fetchTranscripts(currentPage + 1, true);
    }
  };

  // Title editing functions
  const handleTitleClick = (transcriptId, currentName) => {
    setEditingTitle(transcriptId);
    setEditingName(currentName);
    setNameValidationError('');
  };

  const validateTranscriptName = (name, currentId) => {
    if (!name || name.trim().length === 0) {
      return 'Name cannot be empty';
    }

    if (name.trim().length > 100) {
      return 'Name cannot exceed 100 characters';
    }

    // Check for uniqueness
    const existingTranscript = transcripts.find(t =>
      t.id !== currentId && t.name.toLowerCase().trim() === name.toLowerCase().trim()
    );

    if (existingTranscript) {
      return 'A transcript with this name already exists';
    }

    return '';
  };

  const handleTitleSave = async (newName) => {
    const trimmedName = newName.trim();
    const validationError = validateTranscriptName(trimmedName, editingTitle);

    if (validationError) {
      setNameValidationError(validationError);
      return;
    }

    try {
      const response = await fetch(`/api/transcripts/${editingTitle}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: trimmedName })
      });

      if (response.ok) {
        setTranscripts(prev =>
          prev.map(t => t.id === editingTitle ? { ...t, name: trimmedName } : t)
        );
        setEditingTitle(null);
        setEditingName('');
        setNameValidationError('');
      } else {
        setNameValidationError('Failed to update transcript name');
      }
    } catch (error) {
      setNameValidationError('Error updating transcript name');
    }
  };

  const handleTitleCancel = () => {
    setEditingTitle(null);
    setEditingName('');
    setNameValidationError('');
  };

  const handleDeleteTranscript = async (transcriptId) => {
    // Check user preference for delete confirmation
    const preferences = JSON.parse(localStorage.getItem('userPreferences') || '{}');
    const confirmDelete = preferences.confirmTranscriptDelete !== false; // Default to true

    if (confirmDelete) {
      const confirmed = await showConfirm(
        'Delete Transcript',
        'Are you sure you want to delete this transcript? This action cannot be undone. Note: Deleted transcripts will not be removed from your usage quota.',
        'Delete',
        'Cancel'
      );

      if (!confirmed) {
        return;
      }
    }

    try {
      const response = await fetch(`/api/transcripts/${transcriptId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (response.ok) {
        // Update state to remove the deleted transcript
        setTranscripts(prev => {
          const filtered = prev.filter(t => t.id !== transcriptId);
          debugLog('Filtered transcripts:', filtered.length, 'from', prev.length);
          return filtered;
        });
      } else {
        const errorData = await response.json().catch(() => ({}));
        await showError('Delete Failed', errorData.error || 'Failed to delete transcript');
      }
    } catch (err) {
      console.error('Delete error:', err);
      await showError('Delete Error', `Error deleting transcript: ${err.message}`);
    }
  };

  const handleShareTranscript = async (transcriptId) => {
    // Get user's preferred expiration days from localStorage
    const preferences = JSON.parse(localStorage.getItem('userPreferences') || '{}');
    const expirationDays = preferences.shareLinkExpirationDays || 30;

    // Combined privacy warning and audio options
    const audioChoice = await showConfirm(
      'Share Transcript',
      `⚠️ Link valid ${expirationDays} days - anyone can access`,
      {
        confirmText: 'With Audio',
        cancelText: 'Text Only',
        thirdButtonText: 'Cancel'
      }
    );

    // audioChoice: true = With Audio, false = Text Only, null = Cancel/X/Escape
    if (audioChoice === null) {
      return; // User canceled
    }

    const shareWithAudio = audioChoice;

    // Find the transcript data for the share card
    const transcript = transcripts.find(t => t.id === transcriptId);

    try {
      const response = await fetch(`/api/transcripts/${transcriptId}/share`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          permissions: 'view',
          expiresIn: `${expirationDays}d`,
          includeAudio: shareWithAudio
        })
      });

      if (response.ok) {
        const { shareUrl, expiresAt, audioUrl } = await response.json();
        const audioText = shareWithAudio ? 'with audio' : 'without audio';

        // Add UTM parameters for Google Analytics tracking
        const urlWithTracking = new URL(shareUrl);
        urlWithTracking.searchParams.append('utm_source', 'share');
        urlWithTracking.searchParams.append('utm_medium', 'link');
        urlWithTracking.searchParams.append('utm_campaign', 'transcript_share');
        urlWithTracking.searchParams.append('utm_content', shareWithAudio ? 'with_audio' : 'text_only');
        const trackedUrl = urlWithTracking.toString();

        // Calculate expiry message
        const expiryDate = new Date(expiresAt);
        const now = new Date();
        const diffMs = expiryDate - now;
        const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
        const diffHours = Math.round(diffMs / (1000 * 60 * 60));

        let expiryMessage = `Link expires in ${expirationDays} days`;
        if (diffDays > 1) {
          expiryMessage = `Link expires in ${diffDays} days`;
        } else if (diffHours > 1) {
          expiryMessage = `Link expires in ${diffHours} hours`;
        } else {
          expiryMessage = 'Link expires soon';
        }

        // Extract transcript text for share card
        let transcriptText = '';
        if (transcript?.data?.words) {
          transcriptText = transcript.data.words
            .filter(w => !w.deleted)
            .map(w => w.word)
            .join(' ');
        } else if (transcript?.data?.text) {
          transcriptText = transcript.data.text;
        }

        // Store share data for visual card
        setShareCardData({
          shareUrl: trackedUrl,
          transcriptText,
          transcriptName: transcript?.name || 'Transcript',
          duration: transcript?.duration || 0,
          audioUrl: shareWithAudio ? audioUrl : null
        });

        // Show the share link in a dialog with copy button and visual card option
        await showShareLink('Share Link Created', `${audioText.charAt(0).toUpperCase() + audioText.slice(1)}\n${expiryMessage}`, trackedUrl, () => {
          // Callback to show visual card
          setShowShareCard(true);
        });
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        showError('Share Failed', `Failed to create share link: ${errorData.error}`);
      }
    } catch (err) {
      showError('Share Error', `Error creating share link: ${err.message}`);
    }
  };

  const filteredTranscripts = transcripts
    .filter(t =>
      (t.name?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
      (t.originalFileName?.toLowerCase() || '').includes(searchQuery.toLowerCase())
    )
    .filter(t => !filterTag || (t.tags && t.tags.includes(filterTag)))
    .filter(t => currentFolderId === null || t.folderId === currentFolderId)
    .sort((a, b) => {
      const aVal = a[sortBy];
      const bVal = b[sortBy];
      const comparison = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      return sortOrder === 'asc' ? comparison : -comparison;
    });

  // Bulk selection handlers
  const handleSelectTranscript = useCallback((transcriptId, isShiftClick) => {
    setSelectedTranscripts(prev => {
      if (isShiftClick && prev.length > 0) {
        // Shift-click: select range
        const lastSelected = prev[prev.length - 1];
        const currentIndex = filteredTranscripts.findIndex(t => t.id === transcriptId);
        const lastIndex = filteredTranscripts.findIndex(t => t.id === lastSelected);

        const start = Math.min(currentIndex, lastIndex);
        const end = Math.max(currentIndex, lastIndex);

        const rangeIds = filteredTranscripts
          .slice(start, end + 1)
          .map(t => t.id);

        return [...new Set([...prev, ...rangeIds])];
      } else {
        // Toggle selection
        return prev.includes(transcriptId)
          ? prev.filter(id => id !== transcriptId)
          : [...prev, transcriptId];
      }
    });
  }, [filteredTranscripts]);

  // Keyboard shortcuts for selection
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only handle if not in an input field
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }

      if (e.key === 'a' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        setSelectedTranscripts(filteredTranscripts.map(t => t.id));
      }
      if (e.key === 'Escape') {
        setSelectedTranscripts([]);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [filteredTranscripts]);

  // Drag-and-drop handlers
  const handleTranscriptDragStart = (e, transcriptId) => {
    // If dragging a selected item, drag all selected
    const draggedIds = selectedTranscripts.includes(transcriptId)
      ? selectedTranscripts
      : [transcriptId];

    e.dataTransfer.setData('transcriptIds', JSON.stringify(draggedIds));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDropOnFolder = async (e, folderId) => {
    e.preventDefault();

    // Check if dropping transcripts
    const transcriptIdsData = e.dataTransfer.getData('transcriptIds');
    if (transcriptIdsData) {
      const transcriptIds = JSON.parse(transcriptIdsData);
      try {
        await moveTranscriptsToFolder(transcriptIds, folderId);
        setSelectedTranscripts([]);
        fetchTranscripts();
        showSuccess('Moved', `${transcriptIds.length} item(s) moved successfully`);
      } catch (error) {
        showError('Move Failed', error.message);
      }
    }
  };

  // Bulk operations
  const handleBulkMove = async (targetFolderId) => {
    try {
      await moveTranscriptsToFolder(selectedTranscripts, targetFolderId);
      setSelectedTranscripts([]);
      setIsMoveDialogOpen(false);
      fetchTranscripts();
      showSuccess('Moved', `${selectedTranscripts.length} item(s) moved successfully`);
    } catch (error) {
      showError('Move Failed', error.message);
    }
  };

  const handleBulkDelete = async () => {
    const confirmed = await showConfirm(
      'Delete Selected',
      `Are you sure you want to delete ${selectedTranscripts.length} transcript(s)? This action cannot be undone.`,
      'Delete All',
      'Cancel'
    );

    if (!confirmed) return;

    try {
      await bulkDeleteTranscripts(selectedTranscripts);
      setSelectedTranscripts([]);
      fetchTranscripts();
      showSuccess('Deleted', `${selectedTranscripts.length} item(s) deleted successfully`);
    } catch (error) {
      showError('Delete Failed', error.message);
    }
  };

  // Folder handlers
  const handleCreateFolder = async (name, parentId, color, icon) => {
    await createFolder(name, parentId, color, icon);
    showSuccess('Folder Created', `Folder "${name}" created successfully`);
  };

  const handleDeleteFolder = async (folderId) => {
    try {
      await deleteFolder(folderId);
      showSuccess('Folder Deleted', 'Folder deleted successfully');
    } catch (error) {
      showError('Delete Failed', error.message);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleCloneTranscript = async (transcriptId, transcriptName) => {
    try {
      const clonedTranscript = await cloneTranscript(transcriptId);
      showSuccess('Cloned Successfully', `"${transcriptName}" has been cloned successfully!`);
      navigate(`/?transcript=${clonedTranscript.id}`);
    } catch (error) {
      showError('Clone Failed', `Failed to clone transcript: ${error.message}`);
    }
  };

  // Fetch correction requests
  const fetchCorrectionRequests = useCallback(async () => {
    if (process.env.REACT_APP_ENABLE_HUMAN_CORRECTION !== 'true') return;

    try {
      const response = await fetch('/api/corrections/my-requests', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCorrectionRequests(data.requests || []);
      }
    } catch (err) {
      console.error('Error fetching correction requests:', err);
    }
  }, []);

  // Fetch correction requests on mount
  useEffect(() => {
    if (isAuthenticated && process.env.REACT_APP_ENABLE_HUMAN_CORRECTION === 'true') {
      fetchCorrectionRequests();
    }
  }, [isAuthenticated, fetchCorrectionRequests]);

  if (!user) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="dashboard-page">
      <Header />

      <main className="dashboard-main with-sidebar">
        <FolderSidebar
          folderTree={folderTree}
          currentFolderId={currentFolderId}
          onFolderSelect={setCurrentFolderId}
          onCreateFolder={handleCreateFolder}
          onUpdateFolder={updateFolder}
          onDeleteFolder={handleDeleteFolder}
          onMoveFolder={moveFolder}
          onDrop={handleDropOnFolder}
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        />

        <div className="dashboard-content">
          {/* Mobile sidebar toggle */}
          {isSidebarCollapsed && (
            <button
              className="mobile-sidebar-toggle"
              onClick={() => setIsSidebarCollapsed(false)}
            >
              <ChevronRightIcon className="icon" />
              <span>Folders</span>
            </button>
          )}

          {/* Folder breadcrumb */}
          {currentFolderId && folderPath.length > 0 && (
            <div className="folder-breadcrumb">
              <button onClick={() => setCurrentFolderId(null)}>All Transcripts</button>
              {folderPath.map((folder, index) => (
                <span key={folder.id}>
                  <span className="breadcrumb-separator">/</span>
                  <button
                    onClick={() => setCurrentFolderId(folder.id)}
                    className={index === folderPath.length - 1 ? 'current' : ''}
                  >
                    {folder.name}
                  </button>
                </span>
              ))}
            </div>
          )}

        <div className="dashboard-container">
          <div className="dashboard-header-section">
            <h1>Dashboard</h1>
            <div className="dashboard-title-row">
              <div className="header-actions">
                <Link to="/" className="new-transcript-btn">
                  + New Transcript
                </Link>
                {user && (
                  <div className="subscription-info">
                    <div className="subscription-badge">
                      <span className="tier-label">
                        {user.tier === 'basic' ? 'Basic Plan' :
                         user.tier === 'premium' ? 'Premium Plan' : 'Free Plan'}
                      </span>
                    </div>
                    <Link to="/plans-and-payment" className="upgrade-link">
                      {user.tier === 'free' ? 'Upgrade Plan' : 'Manage Subscription'}
                    </Link>
                  </div>
                )}
              </div>
            </div>
            <div className="dashboard-stats">
              <div className="stat">
                <span className="stat-number">{totalTranscripts}</span>
                <span className="stat-label">Total Transcripts</span>
              </div>
              <div className="stat">
                <span className="stat-number">{transcripts.length}</span>
                <span className="stat-label">Showing</span>
              </div>
              <div className="stat">
                <span className="stat-number">
                  {totalHours}
                </span>
                <span className="stat-label">Total Hours</span>
              </div>
              <div className="stat">
                <span className="stat-number">{monthlyUsage.transcripts}</span>
                <span className="stat-label">This Month</span>
              </div>
            </div>
          </div>

          <QuotaDisplay compact={true} showUpgrade={true} />

          {/* Correction Requests Section */}
          {process.env.REACT_APP_ENABLE_HUMAN_CORRECTION === 'true' && correctionRequests.length > 0 && (
            <div className="correction-requests-section">
              <div className="section-header">
                <h3>Professional Correction Requests</h3>
                <button
                  className="toggle-btn"
                  onClick={() => setShowCorrectionRequests(!showCorrectionRequests)}
                >
                  {showCorrectionRequests ? 'Hide' : `Show (${correctionRequests.length})`}
                </button>
              </div>

              {showCorrectionRequests && (
                <div className="correction-requests-list">
                  {correctionRequests.map(request => (
                    <div key={request.id} className="correction-request-item">
                      <div className="request-info">
                        <span className="request-id">Request #{request.id}</span>
                        <span className={`request-status status-${request.status}`}>
                          {request.status.replace('_', ' ')}
                        </span>
                      </div>
                      <div className="request-details">
                        <span>Cost: ${request.cost_usd}</span>
                        <span>Duration: {formatDuration(request.audio_duration_seconds)}</span>
                        <span>Requested: {formatDate(request.created_at)}</span>
                      </div>
                      {request.status === 'completed' && request.completed_at && (
                        <div className="request-completed">
                          Completed: {formatDate(request.completed_at)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="dashboard-controls">
            <div className="search-section">
              <button
                className="search-icon-btn"
                onClick={() => setIsSearchDialogOpen(true)}
                title="Search transcripts"
              >
                🔍
              </button>
              <input
                type="text"
                placeholder="Search transcripts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input desktop-only"
              />
            </div>
            
            <div className="filter-section">
              <select 
                value={sortBy} 
                onChange={(e) => setSortBy(e.target.value)}
                className="sort-select"
              >
                <option value="createdAt">Date Created</option>
                <option value="updatedAt">Last Modified</option>
                <option value="name">Name</option>
                <option value="duration">Duration</option>
              </select>
              
              <button
                onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                className="sort-order-btn"
              >
                {sortOrder === 'asc' ? '↑' : '↓'}
              </button>

              <Link to="/" className="new-transcript-header-btn" title="New transcript">
                +
              </Link>
            </div>
            
            <Link to="/" className="new-transcript-btn">
              + New Transcript
            </Link>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {isLoading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading your transcripts...</p>
            </div>
          ) : filteredTranscripts.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📝</div>
              <h3>No transcripts yet</h3>
              <p>Upload an audio file to create your first transcript</p>
              <Link to="/" className="cta-button">Get Started</Link>
            </div>
          ) : (
            <div className="transcripts-grid">
              {filteredTranscripts.map((transcript) => (
                <div
                  key={transcript.id}
                  className={`transcript-card ${selectedTranscripts.includes(transcript.id) ? 'selected' : ''}`}
                  draggable
                  onDragStart={(e) => handleTranscriptDragStart(e, transcript.id)}
                >
                  <div className="card-selection">
                    <input
                      type="checkbox"
                      checked={selectedTranscripts.includes(transcript.id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleSelectTranscript(transcript.id, e.shiftKey);
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className="transcript-title-section">
                    <h3
                      className="transcript-title"
                      title="Click to edit"
                      onClick={() => handleTitleClick(transcript.id, transcript.name)}
                    >
                      {transcript.name}
                    </h3>
                  </div>
                  <div className="transcript-menu">
                    <button
                      onClick={() => handleShareTranscript(transcript.id)}
                      className="menu-btn"
                      title="Share"
                    >
                      🔗
                    </button>
                  </div>
                  
                  <div className="transcript-meta">
                    <div className="meta-item">
                      <span className="meta-label">Duration:</span>
                      <span className="meta-value">
                        {formatDuration(transcript.duration || 0)}
                      </span>
                    </div>
                    <div className="meta-item">
                      <span className="meta-label">Created:</span>
                      <span className="meta-value">
                        {formatDate(transcript.createdAt)}
                      </span>
                    </div>
                    {transcript.updatedAt !== transcript.createdAt && (
                      <div className="meta-item">
                        <span className="meta-label">Modified:</span>
                        <span className="meta-value">
                          {formatDate(transcript.updatedAt)}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {transcript.data?.text && (
                    <div className="transcript-preview">
                      <p>
                        {transcript.data.text.substring(0, 150)}{transcript.data.text.length > 150 ? '...' : ''}
                      </p>
                    </div>
                  )}

                  {transcript.tags && transcript.tags.length > 0 && (
                    <div className="transcript-tags">
                      {transcript.tags.map((tag, index) => (
                        <span key={index} className="tag">{tag}</span>
                      ))}
                    </div>
                  )}

                  <div className="card-actions">
                    <Link
                      to={`/?transcript=${transcript.id}`}
                      className="action-btn primary"
                    >
                      Edit
                    </Link>
                    <div className={`dropdown-container ${openDropdownId === transcript.id ? 'open' : ''}`}>
                      <button
                        className="action-btn secondary dropdown-toggle"
                        onClick={() => setOpenDropdownId(openDropdownId === transcript.id ? null : transcript.id)}
                        aria-label="More actions"
                      >
                        ⋯
                      </button>
                      {openDropdownId === transcript.id && (
                        <div className="dropdown-menu">
                          <Link
                            to={`/transcripts/${transcript.id}/versions`}
                            className="dropdown-item"
                            onClick={() => setOpenDropdownId(null)}
                          >
                            History
                          </Link>
                          <button
                            onClick={() => {
                              handleCloneTranscript(transcript.id, transcript.name);
                              setOpenDropdownId(null);
                            }}
                            className="dropdown-item"
                          >
                            Clone
                          </button>
                          <button
                            onClick={() => {
                              handleDeleteTranscript(transcript.id);
                              setOpenDropdownId(null);
                            }}
                            className="dropdown-item dropdown-item-delete"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {/* Load More Button */}
              {hasMoreTranscripts && (
                <div className="load-more-container">
                  <button
                    className="load-more-btn"
                    onClick={loadMoreTranscripts}
                    disabled={isLoadingMore}
                  >
                    {isLoadingMore ? (
                      <>
                        <span className="spinner-small"></span>
                        Loading...
                      </>
                    ) : (
                      `Load More (${transcripts.length} of ${totalTranscriptCount})`
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        </div>
      </main>

      <BulkActionBar
        selectedCount={selectedTranscripts.length}
        onMove={() => setIsMoveDialogOpen(true)}
        onDelete={handleBulkDelete}
        onClearSelection={() => setSelectedTranscripts([])}
      />

      <MoveToFolderDialog
        isOpen={isMoveDialogOpen}
        folderTree={folderTree}
        currentFolderId={currentFolderId}
        selectedCount={selectedTranscripts.length}
        onMove={handleBulkMove}
        onClose={() => setIsMoveDialogOpen(false)}
      />

      <CustomAlert
        isOpen={alert?.isOpen}
        onClose={alert?.onClose}
        onConfirm={alert?.onConfirm}
        onCancel={alert?.onCancel}
        title={alert?.title}
        message={alert?.message}
        type={alert?.type}
        confirmText={alert?.confirmText}
        cancelText={alert?.cancelText}
        thirdButtonText={alert?.thirdButtonText}
        showCancel={alert?.showCancel}
        shareUrl={alert?.shareUrl}
        onCreateVisualCard={alert?.onCreateVisualCard}
      />

      <TranscriptNameEditDialog
        isOpen={editingTitle !== null}
        currentName={editingName}
        onSave={handleTitleSave}
        onClose={handleTitleCancel}
        validationError={nameValidationError}
      />

      {/* Search Dialog for Mobile */}
      {isSearchDialogOpen && (
        <>
          <div className="popup-overlay" onClick={() => setIsSearchDialogOpen(false)}></div>
          <div className="popup-modal">
            <div className="popup-modal-content">
              <button className="popup-close" onClick={() => setIsSearchDialogOpen(false)}>×</button>
              <h4>Search Transcripts</h4>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search transcripts..."
                className="search-dialog-input"
                autoFocus
              />
              <div className="popup-modal-actions">
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setIsSearchDialogOpen(false);
                  }}
                  className="popup-modal-button popup-modal-button-secondary"
                >
                  Clear
                </button>
                <button
                  onClick={() => setIsSearchDialogOpen(false)}
                  className="popup-modal-button popup-modal-button-primary"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Shareable Transcript Card */}
      {showShareCard && shareCardData && (
        <ShareableTranscriptCard
          transcriptText={shareCardData.transcriptText}
          transcriptName={shareCardData.transcriptName}
          duration={shareCardData.duration}
          shareUrl={shareCardData.shareUrl}
          audioUrl={shareCardData.audioUrl}
          theme={shareCardTheme}
          onClose={() => setShowShareCard(false)}
          onCopyLink={() => debugLog('Link copied from share card')}
        />
      )}
    </div>
  );
}

export default Dashboard;
